import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AUTHORSHIPS_LIST_QUERY_KEY,
  AUTHORSHIPS_QUERY_KEY,
  createAuthorship,
  createAuthorshipDetailQueryKey,
  deleteAuthorship,
  fetchAuthorshipById,
  fetchAuthorships,
  fetchAuthorshipsForInjunction,
  fetchAuthorshipsForImposition,
  fetchAuthorshipsForReporting,
  type CreateAuthorshipRequest,
} from "@/api/authorships";
import { invalidateAuthorshipQueries } from "@/features/cle-compliance/hooks/invalidateCleDocumentQueries";
import type {
  AuthorshipEvidenceListModel,
  AuthorshipEvidenceModel,
  AuthorshipQueryParams,
} from "@/features/cle-compliance/models/authorship";
import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";

export {
  AUTHORSHIPS_LIST_QUERY_KEY,
  AUTHORSHIPS_QUERY_KEY,
  createAuthorshipDetailQueryKey,
} from "@/api/authorships";

export type { CreateAuthorshipRequest } from "@/api/authorships";

export const useAuthorships = (params: AuthorshipQueryParams = {}) =>
  useQuery<PaginatedResponse<AuthorshipEvidenceListModel>>({
    queryKey: [...AUTHORSHIPS_LIST_QUERY_KEY, params],
    queryFn: () => fetchAuthorships(params),
    staleTime: 5 * 60 * 1000,
  });

export const useAuthorshipsForReporting = (periodId: string) =>
  useQuery<AuthorshipEvidenceListModel[]>({
    queryKey: [...AUTHORSHIPS_QUERY_KEY, "for-reporting", periodId],
    queryFn: () => fetchAuthorshipsForReporting(periodId),
    staleTime: 5 * 60 * 1000,
  });

export const useAuthorshipsForInjunction = (
  injunctionId: string,
  enabled = true,
) =>
  useQuery<AuthorshipEvidenceListModel[]>({
    enabled: enabled && injunctionId.length > 0,
    queryKey: [...AUTHORSHIPS_QUERY_KEY, "for-injunction", injunctionId],
    queryFn: () => fetchAuthorshipsForInjunction(injunctionId),
    staleTime: 5 * 60 * 1000,
  });

export const useAuthorshipsForImposition = (
  impositionId: string,
  enabled = true,
) =>
  useQuery<AuthorshipEvidenceListModel[]>({
    enabled: enabled && impositionId.length > 0,
    queryKey: [...AUTHORSHIPS_QUERY_KEY, "for-imposition", impositionId],
    queryFn: () => fetchAuthorshipsForImposition(impositionId),
    staleTime: 5 * 60 * 1000,
  });

export const useAuthorship = (id: string) =>
  useQuery<AuthorshipEvidenceModel>({
    enabled: id.length > 0,
    queryKey: createAuthorshipDetailQueryKey(id),
    queryFn: () => fetchAuthorshipById(id),
    staleTime: 5 * 60 * 1000,
  });

export const useCreateAuthorship = () => {
  const queryClient = useQueryClient();

  return useMutation<AuthorshipEvidenceModel, Error, CreateAuthorshipRequest>({
    mutationFn: createAuthorship,
    onSuccess: () => {
      invalidateAuthorshipQueries(queryClient);
    },
  });
};

export const useDeleteAuthorship = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteAuthorship,
    onSuccess: (_data, id) => {
      invalidateAuthorshipQueries(queryClient, id);
    },
  });
};
